import {NatGetAdminUser} from './NatGetAdminUser'
import {NatGetAdminUserList} from './NatGetAdminUserList'
import {NatPostAdminUser, PostAdminUserParams} from './NatPostAdminUser'
import {NatPutAdminUser} from './NatPutAdminUser'
import Http from '../../../../network/Http'
import {ContentType} from '../../../../network/Constants'
import {SortOrder} from '../../../../components/shared/SortOrder'
import {ROLE_FOR_ADMINISTRATOR} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatDeleteAdminUser} from './NatDeleteAdminUser'
import {AdminUserRelatedCondominiumGroup} from './AdminUserRelatedCondominiumGroup'
import {ApiResponse} from '../../../../network/response/ApiResponse'
import {NatGetAzureADUser} from './NatGetAzureADUser'

export interface SearchCondition {
    condominiumGroupId?: number
    condominiumCd?: string
    condominiumName?: string
    roleId?: string
    loginId?: string
    familyName?: string
    firstName?: string
    familyNameKana?: string
    firstNameKana?: string
    loginAvailability?: string
}

export interface PutAdminUserParams {
    adminId: string
    roleId: string
    condominiumGroupId?: number
    loginId: string
    mailAddress: string
    familyName: Nullable<string>
    firstName: Nullable<string>
    familyNameKana: string
    firstNameKana: string
    password?: string
    passwordConfirm?: string
    termStartDate: string
    termEndDate: string
    loginAvailability: string
    condominiumCd: Nullable<string>
    sutekiNetId: Nullable<string>
    handoverCondominiumGroupId: Nullable<number>
    endDateOfHandoverPeriod: Nullable<string>
    version: string
}


export interface UpdateAdminUserPasswordParams {
    password: string
    passwordConfirm: string
    version: number
}

interface SortInfo {
    sortKey: string
    sortOrder: string
}

export default class NatAdminUserRepo {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    // 管理者ユーザ一覧取得
    async getAdminUserList(searchCondition?: SearchCondition,
                           searchConditionSortInfos?: SortOrder[],
    ): Promise<NatGetAdminUserList> {
        let searchConditionAdminUsers: SearchCondition = {}
        const sortInfoList: SortInfo[] = []

        if (searchCondition !== undefined) {
            searchConditionAdminUsers = {
                ...searchCondition,
            }
        }

        if (searchConditionSortInfos) {

            searchConditionSortInfos.forEach((searchSortInfo) => {
                const sortKey: string = searchSortInfo.sortKey
                const sortDirection: string =
                    searchSortInfo.sortDirection === false ? 'asc' : searchSortInfo.sortDirection
                const sortInfo: SortInfo = {
                    sortKey: sortKey,
                    sortOrder: sortDirection,
                }
                sortInfoList.push(sortInfo)
            })
        }

        return await this.http.post(
            `/api/admin/adminUserList`,
            {
                condominiumGroupId: searchConditionAdminUsers.condominiumGroupId !== undefined ?
                    searchConditionAdminUsers.condominiumGroupId : undefined,
                condominiumCd: searchConditionAdminUsers.condominiumCd !== '' ?
                    searchConditionAdminUsers.condominiumCd : undefined,
                condominiumName: searchConditionAdminUsers.condominiumName !== '' ?
                    searchConditionAdminUsers.condominiumName : undefined,
                roleId: searchConditionAdminUsers.roleId !== '' ?
                    searchConditionAdminUsers.roleId : undefined,
                loginId: searchConditionAdminUsers.loginId !== '' ?
                    searchConditionAdminUsers.loginId : undefined,
                familyName: searchConditionAdminUsers.familyName !== '' ?
                    searchConditionAdminUsers.familyName : undefined,
                firstName: searchConditionAdminUsers.firstName !== '' ?
                    searchConditionAdminUsers.firstName : undefined,
                familyNameKana: searchConditionAdminUsers.familyNameKana !== '' ?
                    searchConditionAdminUsers.familyNameKana : undefined,
                firstNameKana: searchConditionAdminUsers.firstNameKana !== '' ?
                    searchConditionAdminUsers.firstNameKana : undefined,
                loginAvailability: searchConditionAdminUsers.loginAvailability !== '' ?
                    searchConditionAdminUsers.loginAvailability : undefined,
                sortInfo: sortInfoList,
            },
            ContentType.JSON)
    }

    // 管理者ユーザ取得
    async getAdminUser(adminId: string): Promise<NatGetAdminUser> {
        return await this.http.get(`/api/admin/adminUser/${adminId}`)
    }

    // 管理者ユーザ取得（ログイン済）
    async getMyAdminUser(): Promise<NatGetAdminUser> {
        return await this.http.get(`/api/admin/adminUser/authed`)
    }

    async getAdminUserRelatedCondominiumGroup(adminId: string): Promise<AdminUserRelatedCondominiumGroup> {
        const response: ApiResponse<AdminUserRelatedCondominiumGroup> =
            await this.http.get(`/api/admin/adminUser/${adminId}/relatedCondominiumGroup`)

        return response.details
    }

    // 管理者ユーザ登録
    async registerAdminUser(requestParams: PostAdminUserParams): Promise<NatPostAdminUser> {
        const reqParamsConvert: PostAdminUserParams = requestParams
        if (requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.LIFE_MANAGER.value &&
            requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.CONCIERGE.value &&
            requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC.value) {
            reqParamsConvert.condominiumCd = null
        }
        if (requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC.value) {
            reqParamsConvert.sutekiNetId = null
        }

        return await this.http.post(`/api/admin/adminUser`, reqParamsConvert, ContentType.JSON)
    }

    // 管理者ユーザ更新
    async putAdminUser(requestParams: PutAdminUserParams): Promise<NatPutAdminUser> {
        const reqParamsConvert: PutAdminUserParams = requestParams
        if (requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.LIFE_MANAGER.value &&
            requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.CONCIERGE.value &&
            requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC.value) {
            reqParamsConvert.condominiumCd = null
        }
        if (requestParams.roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC.value) {
            reqParamsConvert.sutekiNetId = null
        }

        return await this.http.put(`/api/admin/adminUser/` + reqParamsConvert.adminId, reqParamsConvert)
    }

    // 管理者ユーザ削除
    async deleteAdminUser(adminId: string): Promise<NatDeleteAdminUser> {
        return await this.http.delete(`/api/admin/adminUser/delete/${adminId}`)
    }

    // 管理者パスワード更新
    async changeAdminUserPassword(requestParams: UpdateAdminUserPasswordParams): Promise<NatPostAdminUser> {
        return await this.http.put(`/api/admin/adminUser/password`, requestParams)
    }

    async getAzureADUser(adminId: string): Promise<NatGetAzureADUser> {
        return await this.http.get(`/api/admin/adminUser/azureAD/${adminId ? adminId : null}`)
    }
}
